'use client';

import { useState, useRef, useEffect, useCallback, memo } from "react";
import MapCard from "@/components/card/map-card/map-card";
import Cta from "@/components/cta/cta";
import Close from "@/components/icon/close";
import Map from "@/components/map/map";
import ZoomControls from "@/components/zoom-controls/zoom-controls";
import { STORIES_BASE_PATH } from "@/constants/routes";
import { useApp } from "@/context/AppContext";


// Zoom configuration
const ZOOM_STEP = 1;
const ZOOM_SCALE = 0.5;
const MAX_ZOOM_STEPS = 2;
const MIN_ZOOM = 1;
const MAX_ZOOM = MIN_ZOOM + (ZOOM_STEP * MAX_ZOOM_STEPS);

const CARD_WIDTH = 200;
const VIEWPORT_PADDING = 16;

function MapLayout({ isDummy = false, stories }) {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedPin, setSelectedPin] = useState(null);
  const mapRef = useRef(null);
  const closeRef = useRef(null);
  const suppressFocusRef = useRef(false);
  const isTouchRef = useRef(false);
  const hoverTimeoutRef = useRef(null);
  const openedViaHoverRef = useRef(false);
  const { previousRouteBeforeMap } = useApp();

  useEffect(() => {
    isTouchRef.current = window.matchMedia('(pointer: coarse)').matches;
  }, []);

  /**
   * Calculates card position above the pin, left-aligned to pin's left edge, clamped to viewport.
   * @param {DOMRect} pinRect
   * @returns {{ left: number, top: number, pinBottom: number }}
   */
  const getCardPosition = useCallback((pinRect) => {
    const left = Math.max(
      VIEWPORT_PADDING,
      Math.min(
        pinRect.left,
        window.innerWidth - CARD_WIDTH - VIEWPORT_PADDING
      )
    );
    const top = pinRect.top - 11;
    const pinBottom = pinRect.bottom + 11;

    return { left, top, pinBottom };
  }, []);

  const handlePinClick = useCallback((index, pin) => {
    if (!isTouchRef.current) {
      document.querySelector('#map-card-popover a')?.click();
      return;
    }
    openedViaHoverRef.current = false;

    if (selectedPin?.index === index) {
      setSelectedPin(null);
      return;
    }

    const rect = mapRef.current?.getPinRect(index);
    if (!rect) return;

    setSelectedPin({ index, ...pin, rect });
  }, [selectedPin?.index]);

  const handlePinMouseEnter = useCallback((index, pin) => {
    if (isTouchRef.current) return;
    clearTimeout(hoverTimeoutRef.current);
    openedViaHoverRef.current = true;

    const rect = mapRef.current?.getPinRect(index);
    if (!rect) return;

    setSelectedPin({ index, ...pin, rect });
  }, []);

  const handlePinMouseLeave = useCallback(() => {
    if (isTouchRef.current) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setSelectedPin(null);
    }, 150);
  }, []);

  const handleCardMouseEnter = useCallback(() => {
    clearTimeout(hoverTimeoutRef.current);
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    if (isTouchRef.current) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setSelectedPin(null);
    }, 150);
  }, []);

  const handlePinFocus = useCallback((index, pin) => {
    if (isTouchRef.current) return;
    
    if (suppressFocusRef.current) {
      suppressFocusRef.current = false;
      return;
    }
    openedViaHoverRef.current = false;

    const rect = mapRef.current?.getPinRect(index);
    if (!rect) return;

    setSelectedPin({ index, ...pin, rect });
  }, []);

  const handleCardClose = useCallback(() => {
    const pinIndex = selectedPin?.index;
    setSelectedPin(null);
    if (pinIndex != null) {
      suppressFocusRef.current = true;
      mapRef.current?.focusPin(pinIndex);
    }
  }, [selectedPin?.index]);

  const handleCardTab = useCallback((shiftKey) => {
    if (selectedPin == null) return;
    const count = mapRef.current?.pinCount ?? 0;
    if (count === 0) return;

    const nextIndex = shiftKey ? selectedPin.index - 1 : selectedPin.index + 1;

    if (nextIndex >= 0 && nextIndex < count) {
      mapRef.current?.focusPin(nextIndex);
    } else {
      setSelectedPin(null);
      if (!shiftKey) {
        closeRef.current?.focus();
      } else {
        mapRef.current?.focus();
      }
    }
  }, [selectedPin]);

  const handleMapInteractionStart = useCallback(() => {
    setSelectedPin(null);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeRef.current?.click();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const zoomIn = () => {
    const newLevel = Math.min(zoomLevel + ZOOM_STEP, MAX_ZOOM);
    if (newLevel === zoomLevel) return null;
    setSelectedPin(null);
    setZoomLevel(newLevel);
    return MIN_ZOOM + (newLevel - MIN_ZOOM) * ZOOM_SCALE;
  };

  const zoomOut = () => {
    setSelectedPin(null);
    setZoomLevel((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  };

  // Close card on window resize
  useEffect(() => {
    if (!selectedPin) return;

    const handleResize = () => setSelectedPin(null);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedPin]);

  // Clean up hover timeout on unmount
  useEffect(() => {
    return () => clearTimeout(hoverTimeoutRef.current);
  }, []);

  const wrapperRef = useRef(null)

  useEffect(() => {
    wrapperRef?.current.focus()
  },[])

  return (
    <div ref={wrapperRef}>
      <Cta
        ref={closeRef}
        className="fixed z-50 top-4 right-4 cta-dark cta-icon-stroke-theme cta-circle-small lg:top-(--space-8) lg:right-(--space-8) lg:cta-circle"
        aria-label="Close map page"
        href={previousRouteBeforeMap || `${STORIES_BASE_PATH}/record-gallery`}
        transitionName="overlay-out"
      >
        <Close className="w-(--space-7) h-auto" aria-hidden="true" />
      </Cta>

      <ZoomControls
        className="absolute z-50 bottom-14 right-4 lg:bottom-6 lg:right-8"
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        zoomLevel={zoomLevel}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        isDark={true}
      />

      <Map
        stories={stories}
        ref={mapRef}
        isDummy={isDummy}
        zoomLevel={zoomLevel}
        zoomScale={MIN_ZOOM + (zoomLevel - MIN_ZOOM) * ZOOM_SCALE}
        onPinClick={handlePinClick}
        onPinFocus={handlePinFocus}
        onPinMouseEnter={handlePinMouseEnter}
        onPinMouseLeave={handlePinMouseLeave}
        selectedPinIndex={selectedPin?.index ?? null}
        onInteractionStart={handleMapInteractionStart}
        onDoubleClickZoom={zoomIn}
      />

      <div className="absolute top-6 left-4 py-1 px-2.5 body-1-small bg-main-light rounded-[1.625] lg:top-11 lg:left-(--space-8) lg:rounded-[2.5] lg:py-2.375 lg:px-5.125 xl:body-1">
        <span className="relative top-[0.5px]">
          <span className="relative hidden pointer-coarse:inline">Tap</span>
          <span className="relative pointer-coarse:hidden">Click</span> a pin to discover a story
        </span>
      </div>

      {selectedPin && (
        <MapCard
          key={selectedPin.index}
          location={selectedPin.location}
          text={selectedPin.text}
          image={selectedPin.mapEntryImage}
          href={selectedPin.link.href}
          position={getCardPosition(selectedPin.rect)}
          onClose={handleCardClose}
          onTab={handleCardTab}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
          autoFocus={!openedViaHoverRef.current}
        />
      )}
    </div>
  )
}

export default memo(MapLayout)